import React, { useEffect, useState, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { INVITE } from '@/constants/testIds';
import {
  Loader2, Copy, Share2, Sparkles, Gift, Users, CheckCircle2, Link2, Mail, Filter, Search,
} from 'lucide-react';

const ACCOUNT_LABEL = {
  artist: 'Artist',
  organizer: 'Event Organizer',
  casting_director: 'Casting Director',
  fan: 'Fan / Discoverer',
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'artist', label: 'Artists' },
  { id: 'organizer', label: 'Organizers' },
  { id: 'casting_director', label: 'Casting Directors' },
  { id: 'fan', label: 'Fans' },
];

const Invite = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/referrals/me');
      let merged = d;
      if (isAdmin) {
        // Admins also see the full platform directory
        const { data: ad } = await api.get('/admin/users');
        merged = { ...d, admin_users: ad.users, admin_counts: ad.counts, admin_total: ad.total };
      }
      setData(merged);
    } catch (e) {
      toast.error('Could not load your referral info');
    } finally { setLoading(false); }
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  // Source list for the bottom directory:
  //  - admins → ALL real platform users (with server-side search via API)
  //  - everyone else → people THEY personally referred
  const directorySource = isAdmin ? (data?.admin_users || []) : (data?.referrals || []);
  const directoryCounts = isAdmin ? (data?.admin_counts || {}) : null;

  const counts = useMemo(() => {
    if (isAdmin && directoryCounts) {
      return {
        all: directoryCounts.all || 0,
        artist: directoryCounts.artist || 0,
        organizer: directoryCounts.organizer || 0,
        casting_director: directoryCounts.casting_director || 0,
        fan: directoryCounts.fan || 0,
      };
    }
    if (!data) return {};
    const c = { all: data.referrals.length, artist: 0, organizer: 0, casting_director: 0, fan: 0 };
    data.referrals.forEach((r) => {
      const t = r.account_type || 'fan';
      if (c[t] != null) c[t] += 1;
      else c.fan += 1;
    });
    return c;
  }, [data, isAdmin, directoryCounts]);

  const filtered = useMemo(() => {
    let rows = directorySource;
    if (filter !== 'all') rows = rows.filter((r) => (r.account_type || 'fan') === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) =>
        (r.name || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.username || '').toLowerCase().includes(q)
      );
    }
    return rows;
  }, [directorySource, filter, search]);

  if (loading || !data) {
    return <div className="pt-32 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-fuchsia-400" /></div>;
  }

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const refUrl = `${origin}/r/${data.username}`;
  const shareText = `Join me on Maarivil — the AI-powered universe for artists, organizers and fans. Sign up with my invite:`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(refUrl);
      setCopied(true);
      toast.success('Invite link copied!');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy. Long-press the link to share.');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on Maarivil', text: shareText, url: refUrl });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const shareTo = (target) => {
    const u = encodeURIComponent(refUrl);
    const t = encodeURIComponent(`${shareText} ${refUrl}`);
    const links = {
      whatsapp: `https://wa.me/?text=${t}`,
      twitter: `https://twitter.com/intent/tweet?text=${t}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      telegram: `https://t.me/share/url?url=${u}&text=${encodeURIComponent(shareText)}`,
      email: `mailto:?subject=${encodeURIComponent('Join me on Maarivil')}&body=${t}`,
    };
    window.open(links[target], '_blank', 'noopener,noreferrer');
  };

  const accountLabel = ACCOUNT_LABEL[user?.account_type] || 'Member';

  return (
    <div data-testid={INVITE.root} className="pt-28 pb-24 max-w-5xl mx-auto px-6 sm:px-12">
      <div className="mb-10">
        <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 mb-3 flex items-center gap-2">
          <Gift className="h-3.5 w-3.5" /> Invite &amp; earn (rewards coming soon)
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight leading-tight">
          Bring the world<br /><span className="rainbow-text">into the spectrum.</span>
        </h1>
        <p className="text-slate-400 mt-4 max-w-2xl text-base">
          Share your personal invite link with artists, organizers, casting directors or fans.
          We&apos;re tracking every signup credited to you — when our rewards program launches, you&apos;ll be first in line.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-12">
        <div className="lg:col-span-2 glass-strong rounded-3xl border border-white/10 p-7 relative overflow-hidden">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-fuchsia-500/20 blur-3xl" />
          <div className="absolute -left-10 -bottom-10 h-40 w-40 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="relative">
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3 flex items-center gap-2">
              <Link2 className="h-3 w-3" /> Your personal invite link
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mb-5">
              <div
                data-testid={INVITE.linkDisplay}
                className="flex-1 glass border border-white/10 rounded-xl px-4 py-3.5 font-mono text-sm text-fuchsia-200 truncate"
              >
                {refUrl}
              </div>
              <button
                data-testid={INVITE.copyButton}
                onClick={copyLink}
                className="btn-rainbow text-white rounded-xl px-5 py-3.5 text-sm font-medium flex items-center justify-center gap-2 min-w-[140px]"
              >
                {copied ? <><CheckCircle2 className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy link</>}
              </button>
            </div>

            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Share via</div>
            <div className="flex flex-wrap gap-2">
              <button data-testid={INVITE.shareWhatsapp} onClick={() => shareTo('whatsapp')} className="glass border border-white/10 hover:border-emerald-400/40 transition rounded-full px-4 py-2 text-sm text-slate-200 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-400" /> WhatsApp
              </button>
              <button data-testid={INVITE.shareTwitter} onClick={() => shareTo('twitter')} className="glass border border-white/10 hover:border-cyan-400/40 transition rounded-full px-4 py-2 text-sm text-slate-200 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-cyan-400" /> X / Twitter
              </button>
              <button data-testid={INVITE.shareLinkedin} onClick={() => shareTo('linkedin')} className="glass border border-white/10 hover:border-blue-400/40 transition rounded-full px-4 py-2 text-sm text-slate-200 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-blue-400" /> LinkedIn
              </button>
              <button data-testid={INVITE.shareFacebook} onClick={() => shareTo('facebook')} className="glass border border-white/10 hover:border-indigo-400/40 transition rounded-full px-4 py-2 text-sm text-slate-200 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-indigo-400" /> Facebook
              </button>
              <button data-testid={INVITE.shareTelegram} onClick={() => shareTo('telegram')} className="glass border border-white/10 hover:border-sky-400/40 transition rounded-full px-4 py-2 text-sm text-slate-200 flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-sky-400" /> Telegram
              </button>
              <button data-testid={INVITE.shareEmail} onClick={() => shareTo('email')} className="glass border border-white/10 hover:border-amber-400/40 transition rounded-full px-4 py-2 text-sm text-slate-200 flex items-center gap-2">
                <Mail className="h-3 w-3" /> Email
              </button>
              <button data-testid={INVITE.shareNative} onClick={nativeShare} className="glass border border-fuchsia-400/40 transition rounded-full px-4 py-2 text-sm text-fuchsia-200 flex items-center gap-2">
                <Share2 className="h-3 w-3" /> More
              </button>
            </div>
          </div>
        </div>

        <div className="glass-strong rounded-3xl border border-white/10 p-7 flex flex-col gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">You are referring as</div>
            <div className="text-white font-display text-xl flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-fuchsia-300" /> {accountLabel}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2">Signups credited to you</div>
            <div data-testid={INVITE.totalSignups} className="font-display text-5xl font-bold rainbow-text">{data.total_signups}</div>
          </div>
          <div className="border border-amber-400/30 bg-amber-400/5 rounded-xl p-3 text-amber-200 text-xs flex items-start gap-2">
            <Gift className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span><strong className="block text-amber-100">Rewards coming soon.</strong>Every credited signup will count when we launch the contributor rewards program.</span>
          </div>
        </div>
      </div>

      <div className="glass rounded-3xl border border-white/10 p-7">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <h2 className="font-display text-2xl text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-cyan-300" />
            {isAdmin ? 'Platform directory' : 'People you brought in'}
          </h2>
          <div className="text-xs text-slate-400">
            {isAdmin
              ? `Admin view · ${data?.admin_total ?? 0} real members`
              : 'Single-level tracking · no multi-tier'}
          </div>
        </div>

        {isAdmin && (
          <div className="text-xs text-slate-400 mb-2">Admin view · {data?.admin_total ?? 0} real members</div>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAdmin
              ? 'Search by name, email or @username…'
              : 'Search the people you referred…'}
            data-testid="invite-admin-search-input"
            className="w-full bg-white/5 border border-white/10 focus:border-fuchsia-400/40 focus:outline-none rounded-xl pl-10 pr-4 py-3 text-sm text-white placeholder:text-slate-500 transition-colors"
          />
        </div>

        {directorySource.length > 0 && (
          <div data-testid={INVITE.filterBar} className="flex items-center gap-2 mb-5 overflow-x-auto pb-2 -mx-1 px-1">
            <Filter className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            {FILTERS.map((f) => {
              const active = filter === f.id;
              const c = counts[f.id] || 0;
              const disabled = c === 0 && f.id !== 'all';
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => !disabled && setFilter(f.id)}
                  data-testid={`${INVITE.filterChip}-${f.id}`}
                  aria-pressed={active}
                  disabled={disabled}
                  className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-medium tracking-wide transition-all border ${
                    active
                      ? 'bg-gradient-to-r from-fuchsia-500/20 to-cyan-500/20 border-fuchsia-400/50 text-white'
                      : disabled
                      ? 'border-white/5 text-slate-600 cursor-not-allowed'
                      : 'border-white/10 text-slate-300 hover:border-fuchsia-400/30 hover:text-white'
                  }`}
                >
                  {f.label}
                  <span className={`ml-1.5 text-[10px] font-mono ${active ? 'text-fuchsia-200' : 'text-slate-500'}`}>{c}</span>
                </button>
              );
            })}
          </div>
        )}

        {directorySource.length === 0 ? (
          <div className="text-slate-400 text-sm py-8 text-center">
            {isAdmin
              ? 'No real members yet. As people sign up they\u2019ll appear here.'
              : 'No one has joined through your link yet. Share it on WhatsApp or Instagram to start your spectrum trail.'}
          </div>
        ) : filtered.length === 0 ? (
          <div data-testid={INVITE.emptyFilter} className="text-slate-400 text-sm py-8 text-center">
            {search.trim()
              ? `No members match \u201c${search.trim()}\u201d.`
              : `No ${FILTERS.find((f) => f.id === filter)?.label.toLowerCase()} ${isAdmin ? 'on the platform yet' : 'have joined through your link yet'}.`}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={INVITE.referralsList}>
            {filtered.map((r, i) => (
              <div key={`${r.user_id || r.email || r.name}-${i}`} className="glass-strong rounded-2xl border border-white/10 p-4 flex items-center gap-3">
                {r.picture
                  ? <img src={r.picture} alt="" className="h-10 w-10 rounded-full object-cover" />
                  : <div className="h-10 w-10 rounded-full bg-gradient-to-br from-fuchsia-500 to-purple-700 flex items-center justify-center text-white text-sm font-bold">{(r.name || '?').charAt(0).toUpperCase()}</div>
                }
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{r.name || 'Anonymous'}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 capitalize">
                    {(r.account_type || 'fan').replace('_', ' ')}
                    {isAdmin && r.email && <span className="ml-1.5 normal-case text-slate-500 lowercase">· {r.email}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Invite;
